/**
 * Typed environment configuration.
 *
 * All environment-specific values are centralized here. Variables are read from
 * Vite's import.meta.env (populated from .env files and CI environment variables)
 * and validated at import time — missing required values fail fast with clear
 * error messages rather than silently producing broken API calls.
 *
 * Never import from import.meta.env directly in app code — use this module instead.
 */

function parseOriginList(raw: string | undefined): readonly string[] {
    if (!raw) return [];
    return raw
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);
}

function requireEnv(key: string): string {
    const value = import.meta.env[key];
    if (!value) {
        throw new Error(
            `Missing required environment variable: ${key}. ` +
                'Check your .env file or CI configuration. ' +
                'See .env.example for documentation.'
        );
    }
    return value;
}

export const env = {
    /** Backend API origin — KMP Ktor client uses this for all HTTP calls. */
    apiBaseUrl: requireEnv('VITE_API_BASE_URL'),

    /**
     * Webapp origin — used for constructing absolute URLs to webapp-served assets
     * (e.g. company logo images). Distinct from apiBaseUrl because in local dev
     * apiBaseUrl points to the Vite proxy (localhost:3778) while static assets
     * are served by the main webapp (http://7shifts.test).
     */
    webappBaseUrl: requireEnv('VITE_WEBAPP_BASE_URL'),

    /** FusionAuth application ID for OAuth authorization code flow. */
    oauthClientId: requireEnv('VITE_OAUTH_CLIENT_ID'),

    /**
     * FusionAuth authority origin (no trailing slash). Used for /oauth2/authorize
     * redirects. Kept separate from apiBaseUrl because FusionAuth runs on its own
     * host/port (e.g. http://7shifts.test:9011 locally) and differs per environment.
     * Token exchange always goes through the webapp proxy (apiBaseUrl).
     */
    oauthAuthority: requireEnv('VITE_OAUTH_AUTHORITY'),

    /**
     * Comma-separated allowlist of origins permitted to send postMessage events
     * to the terminal.
     */
    allowedMessageOrigins: parseOriginList(
        import.meta.env.VITE_ALLOWED_MESSAGE_ORIGINS
    ),

    /** App version injected at build time — sent in user-agent headers. */
    appVersion: import.meta.env.VITE_APP_VERSION || '0.0.0-dev',

    /** OS identifier sent with API requests (e.g. "WEBVIEW"). */
    osVersion: import.meta.env.VITE_OS_VERSION || 'web',

    /** Enables verbose logging in development. */
    debug: import.meta.env.VITE_DEBUG === 'true',

    /** True when running in Vite's dev server (NODE_ENV=development). */
    isDev: import.meta.env.DEV
} as const;
